import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
  Box,
  Typography,
  Chip,
  CircularProgress,
} from '@mui/material';
import { Security as SecurityIcon } from '@mui/icons-material';
import { DialogTitleWithClose } from '../../common';

/**
 * PolicyAssignmentDialog Component
 *
 * Dialog for assigning a content check policy to a template.
 * Policies assigned at template level cascade to all versions.
 * Allows selection of policy and enabling/disabling the assignment.
 * Policy configuration is managed in Settings.
 */
const PolicyAssignmentDialog = ({
  open,
  onClose,
  onAssign,
  policies,
  loading,
  templateName,
}) => {
  const [selectedPolicyId, setSelectedPolicyId] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open) {
      setSelectedPolicyId('');
      setEnabled(true);
      setError(null);
    }
  }, [open]);

  const selectedPolicy = policies.find(p => p.id === selectedPolicyId);

  const handleAssign = async () => {
    if (!selectedPolicyId) {
      setError('Please select a policy');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      await onAssign({
        policy_id: selectedPolicyId,
        enabled,
      });

      onClose();
    } catch (err) {
      setError(err.message || 'Failed to assign policy');
    } finally {
      setSubmitting(false);
    }
  };

  const getDomainColor = domain => {
    const colors = {
      pii: 'primary',
      hipaa: 'success',
      financial: 'warning',
      security: 'error',
      compliance: 'info',
    };
    return colors[domain.toLowerCase()] || 'default';
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitleWithClose
        title={
          <Box>
            <Box display="flex" alignItems="center" gap={1}>
              <SecurityIcon />
              <Typography variant="h6">Assign Content Check Policy</Typography>
            </Box>
            {templateName && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Template: {templateName}
              </Typography>
            )}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 0.5, display: 'block' }}
            >
              Policies assigned at template level apply to all versions
            </Typography>
          </Box>
        }
        onClose={onClose}
        disableClose={submitting}
      />

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Policy Selection */}
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>Select Policy</InputLabel>
          <Select
            value={selectedPolicyId}
            label="Select Policy"
            onChange={e => setSelectedPolicyId(e.target.value)}
            disabled={loading || submitting}
          >
            {policies.length === 0 ? (
              <MenuItem disabled>No policies available</MenuItem>
            ) : (
              policies.map(policy => (
                <MenuItem key={policy.id} value={policy.id}>
                  <Box>
                    <Typography variant="body1">{policy.name}</Typography>
                    {policy.description && (
                      <Typography variant="caption" color="text.secondary">
                        {policy.description}
                      </Typography>
                    )}
                    {policy.domains && policy.domains.length > 0 && (
                      <Box
                        sx={{
                          mt: 0.5,
                          display: 'flex',
                          gap: 0.5,
                          flexWrap: 'wrap',
                        }}
                      >
                        {policy.domains.map(domain => (
                          <Chip
                            key={domain}
                            label={domain.toUpperCase()}
                            size="small"
                            color={getDomainColor(domain)}
                            sx={{ height: 20, fontSize: '0.65rem' }}
                          />
                        ))}
                      </Box>
                    )}
                  </Box>
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>

        {/* Selected Policy Info */}
        {selectedPolicy && (
          <Box
            sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}
          >
            <Typography variant="subtitle2" gutterBottom>
              Policy Details
            </Typography>
            {selectedPolicy.description && (
              <Typography variant="body2" color="text.secondary" paragraph>
                {selectedPolicy.description}
              </Typography>
            )}
            {selectedPolicy.domains && selectedPolicy.domains.length > 0 && (
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
                <Typography variant="caption" sx={{ mr: 0.5 }}>
                  Domains:
                </Typography>
                {selectedPolicy.domains.map(domain => (
                  <Chip
                    key={domain}
                    label={domain.toUpperCase()}
                    size="small"
                    color={getDomainColor(domain)}
                  />
                ))}
              </Box>
            )}
          </Box>
        )}

        {/* Enabled Toggle */}
        <FormControlLabel
          control={
            <Switch
              checked={enabled}
              onChange={e => setEnabled(e.target.checked)}
            />
          }
          label="Enable this assignment"
          sx={{ mb: 2 }}
        />

        <Alert severity="info" sx={{ mt: 2 }}>
          The policy configuration is managed in Settings. You can only
          associate or dis-associate policies here.
        </Alert>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleAssign}
          variant="contained"
          disabled={!selectedPolicyId || submitting || loading}
          startIcon={
            submitting ? <CircularProgress size={20} /> : <SecurityIcon />
          }
        >
          {submitting ? 'Assigning...' : 'Assign Policy'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PolicyAssignmentDialog;
